import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import type { AttendanceStatus } from "@/types/api"
import { shiftIsoDay } from "@/lib/organisation-day"

// Saisie de l'émargement par l'organisme : statuts de `ref_attendance_status`,
// retard en minutes et heure de départ anticipé. Ce sont les deux seules
// données que la vue des heures suivies (`vw_attendance_record_minutes`)
// déduit de la durée du créneau : sans elles, un retard ou un départ ne
// retire rien au certificat de réalisation.

type BadgeVariant = "success" | "destructive" | "outline" | "secondary"

/** Onglet de saisie de l'émargement (`?tab=` absent), le seul où « Enregistrer » s'applique. */
export const ATTENDANCE_DEFAULT_TAB = "emargement"

export interface AttendanceStatusOption {
  code: AttendanceStatus
  label: string
  variant: BadgeVariant
  /** Miroir de `ref_attendance_status.counts_as_present` : le statut compte comme une présence. */
  countsAsPresent: boolean
}

/** Ordre d'affichage : présences, puis absences. */
export const ATTENDANCE_STATUS_OPTIONS: ReadonlyArray<AttendanceStatusOption> = [
  { code: "present", label: "Présent", variant: "success", countsAsPresent: true },
  { code: "late", label: "En retard", variant: "outline", countsAsPresent: true },
  { code: "early_departure", label: "Départ anticipé", variant: "outline", countsAsPresent: true },
  { code: "partial", label: "Présence partielle", variant: "outline", countsAsPresent: true },
  { code: "absent_unjustified", label: "Absent (non justifié)", variant: "destructive", countsAsPresent: false },
  { code: "absent_justified", label: "Absent (justifié)", variant: "secondary", countsAsPresent: false },
  { code: "excused", label: "Excusé", variant: "secondary", countsAsPresent: false },
  { code: "sick_leave", label: "Arrêt maladie", variant: "secondary", countsAsPresent: false },
  { code: "not_registered", label: "Non inscrit", variant: "outline", countsAsPresent: false },
]

/**
 * Statuts retirés de la saisie : le motif médical d'une absence est une
 * donnée de santé (RGPD art. 9), le serveur le refuse (422
 * ATTENDANCE_STATUS_NOT_ALLOWED). L'absence justifiée, avec son justificatif
 * réservé, le remplace. Une ligne antérieure reste lisible.
 */
const NOT_ENTERABLE: ReadonlySet<AttendanceStatus> = new Set<AttendanceStatus>(["sick_leave"])

/**
 * Statuts proposés à la saisie d'une ligne de statut courant `current` : ceux
 * qu'accepte le serveur, plus le statut courant s'il n'en fait plus partie
 * (proposé désactivé, pour que la ligne reste lisible). `none` : ligne neuve.
 */
export function attendanceEntryOptions(
  current: O.Option<AttendanceStatus>,
): ReadonlyArray<AttendanceStatusOption & { disabled: boolean }> {
  return ATTENDANCE_STATUS_OPTIONS.flatMap((option) => {
    const enterable = !NOT_ENTERABLE.has(option.code)
    const isCurrent = pipe(current, O.exists((code) => code === option.code))
    return enterable || isCurrent ? [{ ...option, disabled: !enterable }] : []
  })
}

const OPTION_BY_CODE = new Map<string, AttendanceStatusOption>(
  ATTENDANCE_STATUS_OPTIONS.map((option) => [option.code, option]),
)

/** Libellé et badge d'un code renvoyé par l'API ; un code inconnu reste lisible. */
export function attendanceStatusDisplay(code: string): { label: string; variant: BadgeVariant } {
  return pipe(
    O.fromNullable(OPTION_BY_CODE.get(code)),
    O.match(
      () => ({ label: code, variant: "outline" as const }),
      (option) => ({ label: option.label, variant: option.variant }),
    ),
  )
}

export function countsAsPresent(status: AttendanceStatus): boolean {
  return pipe(
    O.fromNullable(OPTION_BY_CODE.get(status)),
    O.exists((option) => option.countsAsPresent),
  )
}

export function isAttendanceStatus(code: string): code is AttendanceStatus {
  return OPTION_BY_CODE.has(code)
}

/**
 * Compteurs de l'écran d'émargement. `none` = statut inconnu de l'écran : il
 * n'est compté ni présent ni absent (jamais une présence par défaut).
 * « Présents » compte tout statut qui vaut présence (retard, présence
 * partielle et départ anticipé compris), si bien que présents et absents
 * couvrent tous les statuts connus ; « En retard » en est le détail.
 */
export function attendanceCounts(statuses: ReadonlyArray<O.Option<AttendanceStatus>>): {
  present: number
  absent: number
  late: number
} {
  const known = statuses.flatMap((status) => (O.isSome(status) ? [status.value] : []))
  return {
    present: known.filter((status) => countsAsPresent(status)).length,
    absent: known.filter((status) => !countsAsPresent(status)).length,
    late: known.filter((status) => status === "late").length,
  }
}

/** Le retard se saisit en minutes, seulement pour un stagiaire en retard. */
export function needsLateMinutes(status: AttendanceStatus): boolean {
  return status === "late"
}

/** L'heure de départ se saisit pour un départ anticipé ou une présence partielle. */
export function needsDepartureTime(status: AttendanceStatus): boolean {
  return status === "early_departure" || status === "partial"
}

/**
 * Borne du retard saisissable : la plus longue journée de formation.
 * Miroir de `MAX_LATE_DURATION_MINUTES` (`attendance/records.rs`).
 */
export const MAX_LATE_MINUTES = 720

/** Brouillon d'une ligne d'émargement, tel que saisi dans les champs. */
export interface AttendanceDraft {
  status: AttendanceStatus
  /** Champ texte des minutes de retard (vide = non renseigné). */
  lateMinutes: string
  /** Champ `HH:MM` de l'heure de départ (vide = non renseignée). */
  departureTime: string
  /** Présence émargée sur la feuille papier (modifiable tant que la feuille n'est pas finalisée). */
  paper: boolean
}

/**
 * Mise à jour envoyée à l'API. Pour un statut qui porte la donnée (retard pour
 * « En retard », départ pour un départ anticipé ou une présence partielle),
 * `none` signifie « champ vidé » : la mise à jour envoie `null` pour effacer
 * la valeur enregistrée. Pour un autre statut, la donnée n'est pas transmise
 * (le serveur l'efface lui-même).
 */
export interface AttendanceUpdate {
  status_code: AttendanceStatus
  late_duration_minutes: O.Option<number>
  check_out_time: O.Option<string>
}

export function parseLateMinutes(input: string): E.Either<string, O.Option<number>> {
  const trimmed = input.trim()
  if (trimmed === "") return E.right(O.none)
  if (!/^\d{1,4}$/.test(trimmed)) return E.left("Retard : saisissez un nombre entier de minutes.")
  const minutes = Number(trimmed)
  if (minutes > MAX_LATE_MINUTES) return E.left(`Retard : ${MAX_LATE_MINUTES} minutes au maximum.`)
  return E.right(O.some(minutes))
}

/** `HH:MM` saisi → `HH:MM:00` attendu par l'API. */
export function parseDepartureTime(input: string): E.Either<string, O.Option<string>> {
  const trimmed = input.trim()
  if (trimmed === "") return E.right(O.none)
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(trimmed)
  return pipe(
    O.fromNullable(match),
    O.match(
      () => E.left("Heure de départ : format HH:MM attendu."),
      () => E.right(O.some(`${trimmed}:00`)),
    ),
  )
}

/**
 * Traduit un brouillon en mise à jour. Le retard n'est transmis que pour un
 * statut « En retard », l'heure de départ que pour un départ anticipé ou une
 * présence partielle : une valeur restée dans un champ masqué n'est jamais
 * envoyée.
 */
export function buildAttendanceUpdate(draft: AttendanceDraft): E.Either<string, AttendanceUpdate> {
  const late = needsLateMinutes(draft.status) ? parseLateMinutes(draft.lateMinutes) : E.right(O.none)
  const departure = needsDepartureTime(draft.status) ? parseDepartureTime(draft.departureTime) : E.right(O.none)
  return pipe(
    late,
    E.chain((lateMinutes) =>
      pipe(
        departure,
        E.map((checkOut) => ({
          status_code: draft.status,
          late_duration_minutes: lateMinutes,
          check_out_time: checkOut,
        })),
      ),
    ),
  )
}

/** Heure de l'API (`HH:MM:SS`) → valeur d'un champ `type="time"` (`HH:MM`). */
export function timeInputValue(apiTime: O.Option<string>): string {
  return pipe(
    apiTime,
    O.map((time) => time.slice(0, 5)),
    O.getOrElse(() => ""),
  )
}

// ---------------------------------------------------------------------------
// Création de feuille : première demi-journée sans feuille
// ---------------------------------------------------------------------------

export type SlotPeriodCode = "am" | "pm" | "full"

export const SLOT_PERIOD_OPTIONS: ReadonlyArray<{ code: SlotPeriodCode; label: string }> = [
  { code: "full", label: "Journée" },
  { code: "am", label: "Matin" },
  { code: "pm", label: "Après-midi" },
]

/** Une session de formation dépasse rarement un an : borne du parcours des dates. */
const MAX_SCANNED_DAYS = 366

/**
 * Première demi-journée de la session qui n'a pas encore de feuille :
 * une date sans feuille → journée ; une date qui n'a que le matin →
 * après-midi (et inversement) ; une date couverte → date suivante. Une
 * session entièrement couverte retombe sur son premier jour, en journée.
 */
export function firstFreeSheetSlot(
  startDate: string,
  endDate: string,
  sheets: ReadonlyArray<{ sheet_date: string; slot_period: string }>,
): { date: string; period: SlotPeriodCode } {
  const periodsByDate = new Map<string, Set<string>>()
  for (const sheet of sheets) {
    const date = sheet.sheet_date.slice(0, 10)
    const periods = pipe(
      O.fromNullable(periodsByDate.get(date)),
      O.getOrElse(() => new Set<string>()),
    )
    periods.add(sheet.slot_period)
    periodsByDate.set(date, periods)
  }
  const first = startDate.slice(0, 10)
  const last = endDate.slice(0, 10)
  for (let offset = 0; offset < MAX_SCANNED_DAYS; offset++) {
    const date = shiftIsoDay(first, offset)
    if (date > last) break
    const taken = pipe(
      O.fromNullable(periodsByDate.get(date)),
      O.getOrElse(() => new Set<string>()),
    )
    if (taken.has("full") || (taken.has("am") && taken.has("pm"))) continue
    if (taken.has("am")) return { date, period: "pm" }
    if (taken.has("pm")) return { date, period: "am" }
    return { date, period: "full" }
  }
  return { date: first, period: "full" }
}

// ---------------------------------------------------------------------------
// Horaires prévus : seule source de durée d'une feuille sans créneau
// ---------------------------------------------------------------------------

/** Heure de bascule matin / après-midi d'un créneau (miroir de `attendance_slot_in_sheet_period`, migration 281). */
export const AFTERNOON_START = "13:00"

export interface SlotLike {
  slot_date: string
  start_time: string
  is_cancelled: boolean
  is_break: boolean
}

/**
 * La session a-t-elle un créneau de formation (ni annulé, ni pause) qui
 * relève de cette feuille ? Une feuille « Journée » prend tous les créneaux
 * de la date ; une demi-journée, ceux qui commencent avant 13:00 (matin) ou
 * à partir de 13:00 (après-midi). Même règle que la vue des heures suivies :
 * sans créneau, la feuille n'a de durée que par ses horaires prévus.
 */
export function sessionHasSlotFor(slots: ReadonlyArray<SlotLike>, date: string, period: SlotPeriodCode): boolean {
  return slots.some((slot) => {
    if (slot.is_cancelled || slot.is_break || slot.slot_date.slice(0, 10) !== date) return false
    if (period === "full") return true
    const morning = slot.start_time.slice(0, 5) < AFTERNOON_START
    return period === "am" ? morning : !morning
  })
}

export interface ScheduledTimes {
  /** `HH:MM:00` */
  start: string
  /** `HH:MM:00` */
  end: string
}

/** Horaires prévus saisis (`HH:MM`) → horaires transmis, fin strictement après le début. */
export function parseScheduledTimes(start: string, end: string): E.Either<string, ScheduledTimes> {
  const pattern = /^([01]\d|2[0-3]):[0-5]\d$/
  const startTrimmed = start.trim()
  const endTrimmed = end.trim()
  if (!pattern.test(startTrimmed) || !pattern.test(endTrimmed)) {
    return E.left("Indiquez l'heure de début et l'heure de fin prévues (HH:MM).")
  }
  if (endTrimmed <= startTrimmed) {
    return E.left("L'heure de fin prévue doit suivre l'heure de début.")
  }
  return E.right({ start: `${startTrimmed}:00`, end: `${endTrimmed}:00` })
}

/**
 * Bouton de scan d'une feuille. Avant finalisation, le scan se joint et se
 * remplace (Formation › Modifier). Une feuille finalisée sans scan accepte un
 * scan de rattrapage (Formation › Transition), qui vaut signature pour
 * toutes ses présences ; une fois joint, il n'est plus remplaçable. Preuves
 * de la session effacées : plus aucun scan (409 SESSION_EVIDENCE_PURGED).
 */
export type ScanUploadOffer = "none" | "attach" | "replace"

export function scanUploadOffer(sheet: {
  isFinalized: boolean
  hasScan: boolean
  canWrite: boolean
  canTransition: boolean
  evidencePurged: boolean
}): ScanUploadOffer {
  if (sheet.evidencePurged) return "none"
  if (sheet.isFinalized) return !sheet.hasScan && sheet.canTransition ? "attach" : "none"
  if (!sheet.canWrite) return "none"
  return sheet.hasScan ? "replace" : "attach"
}

/**
 * Droits requis nommés à la place des actions de scan masquées. Avant
 * finalisation, joindre ou remplacer le scan demande Formation › Modifier ;
 * télécharger un scan joint demande Documents › Lire. Le rattrapage d'une
 * feuille finalisée nomme déjà Formation › Transition dans son message.
 */
export function scanRequiredRights(sheet: {
  isFinalized: boolean
  hasScan: boolean
  canWrite: boolean
  canReadDocuments: boolean
  evidencePurged: boolean
}): { upload: O.Option<string>; download: O.Option<string> } {
  return {
    upload:
      !sheet.isFinalized && !sheet.canWrite && !sheet.evidencePurged
        ? O.some(`${sheet.hasScan ? "Remplacer" : "Joindre"} le scan : droit requis Formation › Modifier`)
        : O.none,
    download:
      sheet.hasScan && !sheet.canReadDocuments ? O.some("Télécharger : droit requis Documents › Lire") : O.none,
  }
}
